import { formatCount, pluralCategory } from '@/i18n/format.ts'
import type { Messages } from '@/i18n/messages/en/index.ts'

/** The locale this file IS — see the note in the English sibling. */
const LOCALE = 'pt-BR'

const counted = (n: number, one: string, other: string): string =>
  `${formatCount(LOCALE, n)} ${pluralCategory(LOCALE, n) === 'one' ? one : other}`

/**
 * The shared skeleton of the four capacity refusals.
 *
 * No pronoun, in either branch, and that is the whole reason this reads as Portuguese: the
 * English says "the most YOU can have" / "the most IT can have" / "the most THEY can have"
 * because English has a genderless third person to reach for. Here the owner may be a group
 * (masculine) or a person whose gender nobody recorded, so "o máximo permitido" carries the
 * same meaning and agrees with nothing — which is the designed-out half of D-21 rather than
 * a shortcut. `ownerKind` therefore goes unread in this locale, on purpose.
 */
const standing = (held: number, adding: number, limit: number, one: string, other: string): string => {
  if (held === limit) return `${counted(held, one, other)}, o máximo permitido.`
  if (held > limit) return `${counted(held, one, other)} e pode ter no máximo ${formatCount(LOCALE, limit)}.`
  const demais = pluralCategory(LOCALE, adding) === 'one' ? 'seria demais' : 'seriam demais'
  return (
    `${counted(held, one, other)} e pode ter no máximo ${formatCount(LOCALE, limit)}, ` +
    `então mais ${formatCount(LOCALE, adding)} ${demais}.`
  )
}

const BASE: [string, string] = ['base de conhecimento', 'bases de conhecimento']
const AGENT: [string, string] = ['agente', 'agentes']

const RAISE = 'ou peça a um administrador para aumentar o limite.'
const ELSEWHERE = `Escolha a quem isso deve pertencer, ${RAISE}`

/** What the sharing domain's refusals say, in Brazilian Portuguese (checked against the English shape at compile time). */
export const sharing: Pick<
  Messages['errors'],
  | 'sharing.request_not_found'
  | 'sharing.request_already_decided'
  | 'sharing.request_not_waiting'
  | 'sharing.request_not_opened'
  | 'sharing.request_changed_while_reading'
  | 'sharing.only_admin_can_decide'
  | 'sharing.only_admin_can_review'
  | 'sharing.owner_cannot_own'
  | 'sharing.your_base_limit_reached'
  | 'sharing.owner_base_limit_reached'
  | 'sharing.your_agent_limit_reached'
  | 'sharing.owner_agent_limit_reached'
> = {
  'sharing.request_not_found': () => 'Não existe nenhuma solicitação de compartilhamento com esse id.',
  // Every participle here agrees with "solicitação", which is feminine — *aprovada*,
  // *recusada*, *encerrada*. An English enum interpolated into a Portuguese sentence could
  // not have produced one of them (D-20).
  'sharing.request_already_decided': ({ status }) =>
    (
      ({
        approved: 'Esta solicitação já foi aprovada.',
        declined: 'Esta solicitação já foi recusada.',
        withdrawn: 'Esta solicitação já foi retirada.',
        superseded: 'Esta solicitação já foi substituída.',
        closed: 'Esta solicitação já foi encerrada.'
      }) satisfies Record<typeof status, string>
    )[status],
  'sharing.request_not_waiting': () => 'Esta solicitação foi decidida há pouco. Recarregue para ver o resultado.',
  'sharing.request_not_opened': () => 'Não foi possível abrir esta solicitação. Tente de novo.',
  'sharing.request_changed_while_reading': () =>
    'Esta solicitação mudou enquanto você a lia. Recarregue e decida sobre o que ela pede agora.',

  'sharing.only_admin_can_decide': () => 'Só administradores podem decidir uma solicitação de compartilhamento.',
  'sharing.only_admin_can_review': () => 'Só administradores podem revisar solicitações de compartilhamento.',

  'sharing.owner_cannot_own': () => 'Essa conta não pode ser dona de nada.',
  'sharing.your_base_limit_reached': ({ held, adding, limit }) =>
    `Você já tem ${standing(held, adding, limit, ...BASE)} Exclua uma que você não usa mais, ${RAISE}`,
  'sharing.owner_base_limit_reached': ({ owner, held, adding, limit }) =>
    `${owner} já tem ${standing(held, adding, limit, ...BASE)} ${ELSEWHERE}`,
  'sharing.your_agent_limit_reached': ({ held, adding, limit }) =>
    `Você já tem ${standing(held, adding, limit, ...AGENT)} Aposente um que você não usa mais, ${RAISE}`,
  'sharing.owner_agent_limit_reached': ({ owner, held, adding, limit }) =>
    `${owner} já tem ${standing(held, adding, limit, ...AGENT)} ${ELSEWHERE}`
}
